/**
 * PLC 测试面板主组件
 * 
 * 架构：
 * ┌─────────────────────────────────────────────────────────┐
 * │                    PlcTestDrawer                        │
 * │  ┌─────────────────────────────────────────────────────┤
 * │  │  Header: 标题 + 统计 + 操作按钮                      │
 * │  ├─────────────────────────────────────────────────────┤
 * │  │                                                     │
 * │  │  ┌──────────────┐  ┌────────────────────────────┐  │
 * │  │  │ TestCaseList │  │     TestCaseEditor         │  │
 * │  │  │              │  │  ┌──────────────────────┐  │  │
 * │  │  │  - 用例1     │  │  │  输入变量表格        │  │  │
 * │  │  │  - 用例2     │  │  ├──────────────────────┤  │  │
 * │  │  │  - ...       │  │  │  期望输出表格        │  │  │
 * │  │  │              │  │  └──────────────────────┘  │  │
 * │  │  └──────────────┘  └────────────────────────────┘  │
 * │  │                                                     │
 * │  ├─────────────────────────────────────────────────────┤
 * │  │                 TestResultView                      │
 * │  │  运行结果展示区域                                    │
 * │  └─────────────────────────────────────────────────────┘
 * └─────────────────────────────────────────────────────────┘
 */

import React, { useEffect, useCallback } from 'react';
import type { PlcLanguageType } from '../../types';
import { usePlcTestRunner } from '../../hooks/usePlcTestRunner';
import { TestCaseList } from './TestCaseList';
import { TestCaseEditor } from './TestCaseEditor';
import { TestResultView } from './TestResultView';
import './styles.css';

// ============================================
// 类型定义
// ============================================

export interface PlcTestDrawerProps {
  /** 是否可见 */
  readonly visible: boolean;
  /** 关闭回调 */
  readonly onClose: () => void;
  /** 当前代码 */
  readonly code: string;
  /** 当前语言 */
  readonly language: PlcLanguageType;
}

// ============================================
// 子组件：抽屉头部
// ============================================

interface DrawerHeaderProps {
  readonly onClose: () => void;
  readonly testCount: number;
  readonly passedCount: number;
  readonly failedCount: number;
  readonly isRunning: boolean;
  readonly onRunAll: () => void;
  readonly onClearAll: () => void;
}

const DrawerHeader: React.FC<DrawerHeaderProps> = ({
  onClose,
  testCount,
  passedCount,
  failedCount,
  isRunning,
  onRunAll,
  onClearAll
}) => {
  return (
    <div className="plc-test-drawer-header">
      <div className="plc-test-drawer-title">
        <i className="fa fa-flask" />
        <span>仿真测试</span>
        {testCount > 0 && (
          <span className="plc-test-stats">
            <span className="plc-test-stat total">{testCount} 个用例</span>
            {passedCount > 0 && (
              <span className="plc-test-stat passed">
                <i className="fa fa-check-circle" /> {passedCount}
              </span>
            )}
            {failedCount > 0 && (
              <span className="plc-test-stat failed">
                <i className="fa fa-times-circle" /> {failedCount}
              </span>
            )}
          </span>
        )}
      </div>
      
      <div className="plc-test-drawer-actions">
        <button
          type="button"
          className="plc-test-btn plc-test-btn-primary"
          onClick={onRunAll}
          disabled={isRunning || testCount === 0}
          title="运行所有测试"
        >
          <i className={`fa fa-${isRunning ? 'spinner fa-spin' : 'play'}`} />
          <span>{isRunning ? '运行中...' : '全部运行'}</span>
        </button>
        
        <button
          type="button"
          className="plc-test-btn"
          onClick={onClearAll}
          disabled={isRunning || testCount === 0}
          title="清空所有用例"
        >
          <i className="fa fa-trash" />
        </button>
        
        <button
          type="button"
          className="plc-test-btn plc-test-btn-close"
          onClick={onClose}
          title="关闭"
        >
          <i className="fa fa-times" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// 子组件：空状态
// ============================================

interface EmptyStateProps {
  readonly hasVariables: boolean;
  readonly onAddTestCase: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  hasVariables,
  onAddTestCase
}) => {
  if (!hasVariables) {
    return (
      <div className="plc-test-empty">
        <i className="fa fa-exclamation-triangle" />
        <p>未检测到输入/输出变量</p>
        <p className="plc-test-empty-hint">
          请确保代码中包含 VAR_INPUT、VAR_OUTPUT 或 VAR_IN_OUT 声明块
        </p>
      </div>
    );
  }
  
  return (
    <div className="plc-test-empty">
      <i className="fa fa-plus-circle" />
      <p>暂无测试用例</p>
      <button
        type="button"
        className="plc-test-btn plc-test-btn-primary"
        onClick={onAddTestCase}
      >
        <i className="fa fa-plus" />
        <span>添加第一个测试用例</span>
      </button>
    </div>
  );
};

// ============================================
// 主组件：测试抽屉
// ============================================

export const PlcTestDrawer: React.FC<PlcTestDrawerProps> = ({
  visible,
  onClose,
  code,
  language
}) => {
  // 使用测试运行 Hook
  const {
    testCases,
    results,
    parseResult,
    isRunning,
    selectedCaseId,
    parseCode,
    addTestCase,
    deleteTestCase,
    updateTestCase,
    updateInputValue,
    updateExpectedOutput,
    runTestCase,
    runAllTestCases,
    selectTestCase,
    clearAllTestCases,
    duplicateTestCase
  } = usePlcTestRunner();

  // 记录上次已解析的代码和语言，避免重复解析
  const lastParsedRef = React.useRef<{ code: string; language: PlcLanguageType } | null>(null);
  
    // 当代码或语言真正变化时才重新解析
  useEffect(() => {
    if (!visible || !code) return;
    
    const last = lastParsedRef.current;
    if (last && last.code === code && last.language === language) {
      // 代码没变，不重新解析（保留现有测试用例和结果）
      return;
    }
    
    console.log('PlcTestDrawer useEffect parseCode', { code, language });
    parseCode(code, language);
    lastParsedRef.current = { code, language };
  }, [visible, code, language, parseCode]);
  
  // 运行所有测试
  const handleRunAll = useCallback((): void => {
    runAllTestCases(code, language);
  }, [runAllTestCases, code, language]);
  
  // 运行单个测试
  const handleRunSingle = useCallback((id: string): void => {
    runTestCase(id, code, language);
  }, [runTestCase, code, language]);
  
  // 统计数据
  const passedCount = testCases.filter(tc => tc.status === 'passed').length;
  const failedCount = testCases.filter(tc => tc.status === 'failed').length;
  
  // 当前选中的用例
  const selectedCase = testCases.find(tc => tc.id === selectedCaseId);
  const selectedResult = selectedCaseId ? results[selectedCaseId] : undefined;
  
  // 是否有变量
  const hasVariables = parseResult !== null && 
    (parseResult.inputs.length > 0 || parseResult.outputs.length > 0);
  
  // 不可见时不渲染
  if (!visible) {
    return null;
  }
  
  return (
    <div className="plc-test-drawer-overlay" onClick={onClose}>
      <div 
        className="plc-test-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        <DrawerHeader
          onClose={onClose}
          testCount={testCases.length}
          passedCount={passedCount}
          failedCount={failedCount}
          isRunning={isRunning}
          onRunAll={handleRunAll}
          onClearAll={clearAllTestCases}
        />
        
        <div className="plc-test-drawer-body">
          {testCases.length === 0 ? (
            <EmptyState
              hasVariables={hasVariables}
              onAddTestCase={addTestCase}
            />
          ) : (
            <div className="plc-test-content">
              {/* 左侧：用例列表 */}
              <div className="plc-test-sidebar">
                <TestCaseList
                  testCases={testCases}
                  selectedId={selectedCaseId}
                  results={results}
                  isRunning={isRunning}
                  onSelect={selectTestCase}
                  onAdd={addTestCase}
                  onDelete={deleteTestCase}
                  onDuplicate={duplicateTestCase}
                  onRun={handleRunSingle}
                />
              </div>
              
              {/* 右侧：编辑器和结果 */}
              <div className="plc-test-main">
                {selectedCase && parseResult ? (
                  <>
                    <TestCaseEditor
                      testCase={selectedCase}
                      inputs={parseResult.inputs}
                      outputs={parseResult.outputs}
                      isRunning={isRunning}
                      onUpdateName={(name) => updateTestCase(selectedCase.id, { name })}
                      onUpdateDescription={(desc) => updateTestCase(selectedCase.id, { description: desc })}
                      onUpdateInput={(varName, value) => updateInputValue(selectedCase.id, varName, value)}
                      onUpdateOutput={(varName, value) => updateExpectedOutput(selectedCase.id, varName, value)}
                      onRun={() => handleRunSingle(selectedCase.id)}
                    />
                    
                    {selectedResult && (
                      <TestResultView
                        result={selectedResult}
                        outputs={parseResult.outputs}
                        allVariables={parseResult.variables}
                      />
                    )}
                  </>
                ) : (
                  <div className="plc-test-placeholder">
                    <i className="fa fa-hand-pointer-o" />
                    <p>请从左侧选择一个测试用例</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlcTestDrawer;